import React, { useState, useEffect, useRef } from "react";
import { Lightning, Microphone, PencilSimple } from "phosphor-react";

// Detect if running in webview - SAME LOGIC AS NAVBAR
const isWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check localStorage flag first (can be set by native app)
  // This is the most reliable way for native apps to signal webview
  if (localStorage.getItem('isWebView') === 'true') {
    return true;
  }

  // Check URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('webview') === 'true') {
    return true;
  }

  // React Native WebView detection (most reliable)
  if (window.ReactNativeWebView) {
    return true;
  }

  // Check for custom webview identifier
  if (window.__WEBVIEW__ || window.isWebView) {
    return true;
  }

  // WebKit message handlers (iOS) - only if explicitly set
  if (window.webkit && window.webkit.messageHandlers && Object.keys(window.webkit.messageHandlers).length > 0) {
    return true;
  }

  const userAgent = navigator.userAgent || '';
  const userAgentLower = userAgent.toLowerCase();

  // Android WebView detection - be more specific
  // Android webview has "wv" in user agent AND doesn't have Chrome
  if (userAgentLower.includes('wv') && !userAgentLower.includes('chrome')) {
    return true;
  }

  return false;
};

const LessonBrainIcon = ({
  lessonId,
  onQuickPracticeStart,
  onWritingPracticeStart,
  onSpeakingPracticeStart,
  onLessonClick,
  isActive,
  quickStats = { level: null, progress: null },
  writingStats = { level: null, progress: null },
  speakingStats = { level: null, progress: null },
  loadingStats = false
}) => {
  const [isInWebView, setIsInWebView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Close dropdown when lesson changes or becomes inactive
  useEffect(() => {
    if (!isActive) {
      setOpenDropdown(false);
    }
  }, [isActive, lessonId]);

  // Detect webview - SAME LOGIC AS NAVBAR
  useEffect(() => {
    const webViewDetected = isWebView();
    setIsInWebView(webViewDetected);

    // Re-check periodically in case webview flag is set later
    const interval = setInterval(() => {
      const webViewDetected = isWebView();
      setIsInWebView(webViewDetected);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Check if mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // position state no longer needed - dropdown uses CSS absolute positioning

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpenDropdown(false);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown]);

  const handleBrainClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    console.log('🧠 Brain clicked:', { isActive, openDropdown, lessonId }); // Debug

    const willOpen = !openDropdown;
    console.log('🧠 Will open:', willOpen); // Debug

    // Calculate position for fixed dropdown before opening
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      console.log('🧠 Button rect:', rect); // Debug

      if (isMobile) {
        // Mobile: dropdown appears directly below button, aligned to right edge
        const dropdownWidth = 280; // max-width from CSS
        const leftPosition = Math.max(12, rect.right - dropdownWidth); // Align right edge, but don't go off-screen
        setDropdownPosition({
          top: rect.bottom + 8,
          left: leftPosition
        });
        console.log('🧠 Set position (mobile):', { top: rect.bottom + 8, left: leftPosition }); // Debug
      } else {
        // Desktop: dropdown appears to the right
        setDropdownPosition({
          top: rect.top,
          left: rect.right + 12
        });
        console.log('🧠 Set position (desktop):', { top: rect.top, left: rect.right + 12 }); // Debug
      }
    }

    // If lesson is not active, activate it first
    if (!isActive && onLessonClick && lessonId) {
      console.log('🧠 Activating lesson first'); // Debug
      onLessonClick(lessonId);
      // Still open dropdown after activating lesson
      setTimeout(() => {
        console.log('🧠 Opening dropdown after lesson activation'); // Debug
        setOpenDropdown(true);
      }, 100);
    } else {
      // Toggle dropdown
      console.log('🧠 Toggling dropdown:', willOpen); // Debug
      setOpenDropdown(willOpen);
    }
  };

  const handlePracticeClick = (e, type) => {
    console.log('🎯 Practice clicked:', { type, lessonId }); // Debug
    e.stopPropagation();
    e.preventDefault();
    setOpenDropdown(false);

    // Use setTimeout to ensure state update completes before calling handler
    setTimeout(() => {
      // changed by Cursor: send lessonId as object { lessonId } to match web implementation
      if (type === 'quick' && onQuickPracticeStart) {
        console.log('🚀 Calling onQuickPracticeStart with:', { lessonId }); // Debug
        onQuickPracticeStart({ lessonId });
      } else if (type === 'writing' && onWritingPracticeStart) {
        console.log('✍️ Calling onWritingPracticeStart with:', { lessonId }); // Debug
        onWritingPracticeStart({ lessonId });
      } else if (type === 'speaking' && onSpeakingPracticeStart) {
        console.log('🎤 Calling onSpeakingPracticeStart with:', { lessonId }); // Debug
        onSpeakingPracticeStart({ lessonId });
      }
    }, 0);
  };

  // Only hide if webview, show in both mobile and desktop
  if (isInWebView) {
    console.log('🧠 Hidden because webview detected'); // Debug
    return null;
  }

  console.log('🧠 Rendering brain icon:', { openDropdown, isActive }); // Debug

  const dropdownItemBase = "w-full p-[8px_10px] bg-transparent border-none border-b border-b-[rgba(18,118,109,0.05)] last:border-b-0 text-left cursor-pointer transition-all duration-200 text-[var(--text-primary)] touch-manipulation [-webkit-tap-highlight-color:rgba(18,118,109,0.1)] pointer-events-auto hover:bg-[rgba(18,118,109,0.1)] hover:text-[#12766d] active:bg-[rgba(18,118,109,0.15)] max-md:min-h-[36px]";
  const dropdownItemHighProgress = "bg-[rgba(16,185,129,0.1)] !border-l-[3px] border-l-[#10b981] hover:!bg-[rgba(16,185,129,0.15)] hover:!text-[#065f46] active:!bg-[rgba(16,185,129,0.2)]";

  const getDropdownItemClass = (stats) => {
    const isHighProgress = !loadingStats && stats.progress !== null && stats.progress >= 80;
    return `${dropdownItemBase} ${isHighProgress ? dropdownItemHighProgress : ''}`;
  };

  return (
    <>
      <div
        className="relative flex-shrink-0 ml-auto z-[10001] pointer-events-auto overflow-visible"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <button
          ref={buttonRef}
          type="button"
          className="relative flex-shrink-0 ml-auto z-[10001] flex items-center justify-center bg-gradient-to-br from-[#fcb319] to-[#f5a500] border-none p-[7px_12px] cursor-pointer outline-none pointer-events-auto rounded-md transition-all duration-200 shadow-[0_1px_3px_rgba(252,179,25,0.2)] hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_2px_4px_rgba(252,179,25,0.3)] hover:-translate-y-px active:scale-[0.96] active:translate-y-0 active:shadow-[0_1px_2px_rgba(252,179,25,0.15)] max-md:min-h-8 max-md:p-[6px_10px] max-md:touch-manipulation max-md:select-none max-md:appearance-none max-md:z-[10002] max-md:[-webkit-tap-highlight-color:rgba(252,179,25,0.2)] max-md:active:from-[#e69500] max-md:active:to-[#d48800]"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log('🧠 Button onClick triggered'); // Debug
            handleBrainClick(e);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log('🧠 Button onTouchEnd triggered'); // Debug
            handleBrainClick(e);
          }}
          aria-label="Open practice menu"
        >
          {/* changed by Cursor: replaced clipboard icon with yellow Practice button text */}
          <span className="text-[11px] font-semibold text-[#1d4746] whitespace-nowrap leading-none max-md:text-[10px] max-md:text-[#12766d] max-md:pointer-events-none max-md:relative max-md:z-[1]">Practice</span>
        </button>
        {openDropdown && (
          <div
            ref={dropdownRef}
            className="absolute top-0 left-[calc(100%+12px)] bg-[var(--card-bg)] rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.2)] min-w-[220px] max-w-[260px] z-[99999] overflow-visible animate-slide-right pointer-events-auto border border-[rgba(18,118,109,0.1)] max-md:top-full max-md:left-auto max-md:right-0 max-md:mt-1 max-md:min-w-0 max-md:max-w-[280px] max-md:w-auto max-md:animate-slide-down max-md:rounded-lg"
          >
          <button
            type="button"
            className={getDropdownItemClass(quickStats)}
            onClick={(e) => handlePracticeClick(e, 'quick')}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePracticeClick(e, 'quick');
            }}
          >
            <div className="flex flex-col gap-1 w-full max-md:flex-row max-md:items-center max-md:justify-between max-md:gap-0">
              <div className="flex items-center gap-1.5 text-[12px] font-medium max-md:text-[11px] max-md:gap-[5px]">
                <Lightning size={14} weight="regular" className="flex-shrink-0 text-[#12766d] max-md:w-3 max-md:h-3" />
                <span>Quick Practice</span>
              </div>
              <div className="flex items-center gap-1 max-md:hidden">
                <div className="flex-1 h-1 bg-[rgba(18,118,109,0.1)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#12766d] via-[#236463] to-[#1d4746] rounded-full transition-[width_0.3s_ease]"
                    style={{
                      width: loadingStats || quickStats.progress === null
                        ? '0%'
                        : `${Math.min(100, Math.max(0, quickStats.progress))}%`
                    }}
                  ></div>
                </div>
                <span className="text-[9px] font-semibold text-[var(--text-secondary)] min-w-[28px] text-right">
                  {loadingStats || quickStats.progress === null
                    ? '--%'
                    : `${Math.round(Math.min(100, Math.max(0, quickStats.progress)))}%`}
                </span>
                <button
                  className="py-[3px] px-[10px] bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[#1d4746] border-none rounded-md text-[10px] font-semibold cursor-pointer transition-all duration-200 shadow-[0_1px_3px_rgba(252,179,25,0.2)] whitespace-nowrap flex-shrink-0 hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_2px_4px_rgba(252,179,25,0.3)] hover:-translate-y-px active:translate-y-0 active:scale-[0.98]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePracticeClick(e, 'quick');
                  }}
                >
                  Start
                </button>
              </div>
            </div>
          </button>
          <button
            type="button"
            className={getDropdownItemClass(writingStats)}
            onClick={(e) => handlePracticeClick(e, 'writing')}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePracticeClick(e, 'writing');
            }}
          >
            <div className="flex flex-col gap-1 w-full max-md:flex-row max-md:items-center max-md:justify-between max-md:gap-0">
              <div className="flex items-center gap-1.5 text-[12px] font-medium max-md:text-[11px] max-md:gap-[5px]">
                <PencilSimple size={14} weight="regular" className="flex-shrink-0 text-[#12766d] max-md:w-3 max-md:h-3" />
                <span>Writing Practice</span>
              </div>
              <div className="flex items-center gap-1 max-md:hidden">
                <div className="flex-1 h-1 bg-[rgba(18,118,109,0.1)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#12766d] via-[#236463] to-[#1d4746] rounded-full transition-[width_0.3s_ease]"
                    style={{
                      width: loadingStats || writingStats.progress === null
                        ? '0%'
                        : `${Math.min(100, Math.max(0, writingStats.progress))}%`
                    }}
                  ></div>
                </div>
                <span className="text-[9px] font-semibold text-[var(--text-secondary)] min-w-[28px] text-right">
                  {loadingStats || writingStats.progress === null
                    ? '--%'
                    : `${Math.round(Math.min(100, Math.max(0, writingStats.progress)))}%`}
                </span>
                <button
                  className="py-[3px] px-[10px] bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[#1d4746] border-none rounded-md text-[10px] font-semibold cursor-pointer transition-all duration-200 shadow-[0_1px_3px_rgba(252,179,25,0.2)] whitespace-nowrap flex-shrink-0 hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_2px_4px_rgba(252,179,25,0.3)] hover:-translate-y-px active:translate-y-0 active:scale-[0.98]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePracticeClick(e, 'writing');
                  }}
                >
                  Start
                </button>
              </div>
            </div>
          </button>
          <button
            type="button"
            className={getDropdownItemClass(speakingStats)}
            onClick={(e) => handlePracticeClick(e, 'speaking')}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePracticeClick(e, 'speaking');
            }}
          >
            <div className="flex flex-col gap-1 w-full max-md:flex-row max-md:items-center max-md:justify-between max-md:gap-0">
              <div className="flex items-center gap-1.5 text-[12px] font-medium max-md:text-[11px] max-md:gap-[5px]">
                <Microphone size={14} weight="regular" className="flex-shrink-0 text-[#12766d] max-md:w-3 max-md:h-3" />
                <span>Speaking Practice</span>
              </div>
              <div className="flex items-center gap-1 max-md:hidden">
                <div className="flex-1 h-1 bg-[rgba(18,118,109,0.1)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#12766d] via-[#236463] to-[#1d4746] rounded-full transition-[width_0.3s_ease]"
                    style={{
                      width: loadingStats || speakingStats.progress === null
                        ? '0%'
                        : `${Math.min(100, Math.max(0, speakingStats.progress))}%`
                    }}
                  ></div>
                </div>
                <span className="text-[9px] font-semibold text-[var(--text-secondary)] min-w-[28px] text-right">
                  {loadingStats || speakingStats.progress === null
                    ? '--%'
                    : `${Math.round(Math.min(100, Math.max(0, speakingStats.progress)))}%`}
                </span>
                <button
                  className="py-[3px] px-[10px] bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[#1d4746] border-none rounded-md text-[10px] font-semibold cursor-pointer transition-all duration-200 shadow-[0_1px_3px_rgba(252,179,25,0.2)] whitespace-nowrap flex-shrink-0 hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_2px_4px_rgba(252,179,25,0.3)] hover:-translate-y-px active:translate-y-0 active:scale-[0.98]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePracticeClick(e, 'speaking');
                  }}
                >
                  Start
                </button>
              </div>
            </div>
          </button>
          </div>
        )}
      </div>
    </>
  );
};

export default LessonBrainIcon;
